import { IoPeopleOutline } from 'react-icons/io5'

import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { copyableListView, renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { formatPhone, formatPhones } from '@/Ship/Helpers/formatPhone'
import { exportToExcelButton } from '@/Containers/CrmSection/Components/Export'

import { ClientVerificationButtons } from '../Components/ClientVerification'
import { CLIENT_COST_FIELDS, CLIENT_FIELD_LABELS as L } from '../Contracts/ClientFieldLabels'
import { CLIENT_REST_URL } from '../Contracts/IClient'
import { ClientFormTemplate } from '../Templates/ClientFormTemplate'

import type { ClientStatusRef } from '../Contracts/IClient'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { ReactNode } from 'react'

interface ClientRow {
    title?: unknown
    status?: ClientStatusRef | null
    phones?: string[] | null
    _original?: ClientRow
}

/** Довідник статусів клієнта (`SPEC-007` §5.1) — з нього рядок статусів і поле фільтра. */
const CLIENT_STATUS_RESOURCE = 'Setting.ListClientStatus'

/** Ширина колонки «Назва» з переносом — як у легасі (`ClientPage.jsx:47-56`). */
const TITLE_COLUMN_WIDTH = 150

const sourceOf = (row: unknown): ClientRow => {
    const item = (row ?? {}) as ClientRow

    return item._original ?? item
}

/**
 * Колонки, які ядро само не намалює як треба:
 *
 *  - **«Статус»** — бейдж кольору довідника (`StatusTag`). Ядровий бейдж
 *    шукає статус серед `options` ресурсу за рядком, а в клієнта статус —
 *    вкладений рядок довідника;
 *  - **«Телефони»** — у базі самими цифрами, у колонці — «+38 (067) 123-45-67»;
 *  - **«Назва»** — 150 px з переносом: довгі назви не розтягують таблицю.
 */
const AddClientColumnRenderers = ({ columns }: { columns: Array<Record<string, unknown>> }) => {
    columns.forEach((column) => {
        const key = String(column['key'] ?? '')

        if (key === 'status') {
            column['render'] = (_: unknown, row: unknown): ReactNode => {
                const status = sourceOf(row).status

                return status && typeof status.title === 'string' ? (
                    <StatusTag label={status.title} color={status.color} />
                ) : (
                    '—'
                )
            }
        }

        if (key === 'phones') {
            column['render'] = (_: unknown, row: unknown): ReactNode => formatPhones(sourceOf(row).phones) || '—'
        }

        if (key === 'title') {
            column['width'] = TITLE_COLUMN_WIDTH
            column['render'] = (text: ReactNode): ReactNode => (
                <div style={{ width: TITLE_COLUMN_WIDTH, whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
            )
        }
    })

    return columns
}

/** Сума в гривнях/валюті: 0 за замовчуванням (КЛ-09), у картці — разом із валютою. */
const amountField = (label: string): IFieldConfig => ({
    label,
    type: 'number',
    defaultValue: 0,
    inputProps: { min: 0, precision: 2, style: { width: '100%' } },
})

/** Валюта суми: вузький select із кодом валюти (`SPEC-008` §4.8). */
const currencyField = (): IFieldConfig => ({
    label: 'Валюта',
    type: 'select',
    resource: 'Setting.ListCurrency',
    responseLabel: (item: { code?: unknown; title?: unknown }) =>
        typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
})

/** Лише перегляд: ставить сервер (переходи верифікації, аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Тіло запиту картки перед відправкою.
 *
 *  - порожня сума — 0 (КЛ-09): бек тримає NOT NULL і без цього відповів би 422
 *    на клієнта, у якого суму просто не вводили;
 *  - поля лише для читання бек однаково не приймає (`client:create` /
 *    `client:update`, КЛ-11) — не шлемо їх, щоб тіло запиту не обіцяло зміни,
 *    якої не буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'status',
    'verifiedBy',
    'verifiedAt',
    'commentVerification',
    'isVerified',
    'isBlacklisted',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareClientPayload = ({ values }: { values: Record<string, unknown> }): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    for (const { amount } of CLIENT_COST_FIELDS) {
        const value = values[amount]

        if (value === null || value === undefined || value === '') values[amount] = 0
    }
}

/**
 * Клієнти (`SPEC-009` §7, §8.1–§8.3): реєстр на 13 колонок і 13 фільтрів у
 * порядку легасі (`ClientPage.jsx:30-41`), 20 рядків на сторінку, сортування за
 * «Датою зміни» — на беку.
 *
 * Права — лише з беку (`SPEC-003` §1.3): «Додати» — колекційне `create`
 * (`ResourceCorrespondenceConfig`), «Редагувати» / «Видалити» —
 * `_permissions` запису. Ролей ресурс не питає. Переходи верифікації —
 * пункти випадного списку картки (`ClientVerificationButtons`, `SPEC-009` §8.6),
 * теж лише за `_permissions`.
 *
 * Над таблицею — рядок статусів довідника «Статуси клієнтів» з лічильниками
 * `GET /services/clients/counters` (`StatusFilterBar`, Р9.3). У шапці —
 * «Експорт в Excel» за колекційним правом `export` (`SPEC-009` §12).
 */
export const ClientResource: IResourceConfig = {
    title: 'Клієнти',
    fields: {
        id: {
            label: L.id,
            type: 'number',
            isEditable: false,
        },
        title: {
            label: L.title,
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        fullName: {
            label: L.fullName,
            inputProps: { maxLength: 150 },
        },
        // Статус ставлять створення («Новий») і переходи верифікації — у формі
        // лише перегляд. `inputProps.disabled` вимикає й ядрову колонку, яка
        // інакше слала б `PATCH {status}` (вартовий колонки статусу)
        status: {
            label: L.status,
            type: 'status',
            statusResource: CLIENT_STATUS_RESOURCE,
            isEditable: false,
            inputProps: { disabled: true },
        },
        responsibles: {
            label: L.responsibles,
            type: 'select',
            isMultiple: true,
            isRequired: true,
            // Мультивибір у фільтрі: «будь-хто з обраних» (Р9.5)
            isFilterMultiple: true,
            // Список співробітників ядро віддає лише з активними (`SPEC-005`)
            resource: 'Setting.User',
            view: renderUsersView,
        },
        emails: {
            label: L.emails,
            isMultiple: true,
            // Пошти в базі в нижньому регістрі (`ContactDataNormalizer`)
            filterValueTransform: (value: string) => value.trim().toLowerCase(),
            view: copyableListView(),
        },
        phones: {
            label: L.phones,
            type: 'phone',
            isMultiple: true,
            // У базі самими цифрами — у картці той самий формат, що в реєстрі,
            // і копіювання біля кожного: їх частіше переносять, ніж читають
            view: copyableListView(formatPhone),
        },
        tags: {
            label: L.tags,
            isMultiple: true,
        },
        comment: {
            label: L.comment,
            type: 'textarea',
            inputProps: { maxLength: 4096 },
        },
        formTaxation: {
            label: L.formTaxation,
            type: 'select',
            resource: 'Setting.ListFormTaxation',
        },
        address: {
            label: L.address,
            inputProps: { maxLength: 255 },
        },
        iban: {
            label: L.iban,
            inputProps: { maxLength: 30 },
        },
        bank: {
            label: L.bank,
            inputProps: { maxLength: 150 },
        },
        mfo: {
            label: L.mfo,
            inputProps: { maxLength: 150 },
        },
        edrpou: {
            label: L.edrpou,
            // Точний збіг, як у легасі (`SearchFilter edrpou exact`)
            filterOperator: '=',
            // Формат (8–12 цифр, Q9.2) перевіряє бек — текст 422 ляже під поле
            inputProps: { maxLength: 12 },
        },
        numberVAT: {
            label: L.numberVAT,
            inputProps: { maxLength: 150 },
        },
        ipn: {
            label: L.ipn,
            filterOperator: '=',
            inputProps: { maxLength: 12 },
        },
        manager: {
            label: L.manager,
            inputProps: { maxLength: 100 },
        },
        forwarder: {
            label: L.forwarder,
            type: 'select',
            resource: 'Setting.ListLegalEntity',
        },
        contactPerson: {
            label: L.contactPerson,
            inputProps: { maxLength: 255 },
        },
        positionContactPerson: {
            label: L.positionContactPerson,
            inputProps: { maxLength: 150 },
        },
        contactPersonPhone: {
            label: L.contactPersonPhone,
            type: 'phone',
            view: copyableListView(formatPhone),
        },
        contactPersonEmail: {
            label: L.contactPersonEmail,
            inputProps: { maxLength: 150 },
            view: copyableListView(),
        },
        typeCargo: {
            label: L.typeCargo,
            inputProps: { maxLength: 255 },
        },
        approximateCostCargo: amountField(L.approximateCostCargo),
        approximateCostCargoCurrency: currencyField(),
        routeFirstShipment: {
            label: L.routeFirstShipment,
            inputProps: { maxLength: 255 },
        },
        costFirstShipmentClient: amountField(L.costFirstShipmentClient),
        costFirstShipmentClientCurrency: currencyField(),
        costFirstShipmentCarrier: amountField(L.costFirstShipmentCarrier),
        costFirstShipmentCarrierCurrency: currencyField(),
        clientPaymentTerm: {
            label: L.clientPaymentTerm,
            type: 'number',
            inputProps: { min: 0, precision: 0, style: { width: '100%' } },
        },
        verifiedBy: readOnly({
            label: L.verifiedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        verifiedAt: readOnly({
            label: L.verifiedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        commentVerification: readOnly({
            label: L.commentVerification,
            type: 'textarea',
        }),
        // Службова інформація: бек віддає її для кожного запису (ядрова
        // `ListEntity`, група `list:read`); у формі — лише показ
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    // 13 колонок у порядку легасі (`ClientPage.jsx:37-41`)
    table: {
        showColumns: [
            'title',
            'status',
            'emails',
            'phones',
            'responsibles',
            'edrpou',
            'id',
            'fullName',
            'tags',
            'formTaxation',
            'iban',
            'mfo',
            'ipn',
        ],
        // Статус і телефони малюємо самі — автотрансформація тут лише заважає
        dataSourceNoTransform: ['status', 'phones'],
        eventHandlers: {
            onBuildColumns: { AddClientColumnRenderers },
        },
    },
    // 13 фільтрів у порядку легасі (`ClientPage.jsx:30-34`)
    filter: {
        includeFields: [
            'title',
            'emails',
            'phones',
            'edrpou',
            'id',
            'fullName',
            'status',
            'responsibles',
            'tags',
            'formTaxation',
            'iban',
            'mfo',
            'ipn',
        ],
        // «Статус» — рядок довідника: варіанти бере з того самого довідника,
        // що й рядок статусів над таблицею, і пише код (`StatusFilterBar`)
        fieldProps: {
            status: { input: StatusDictionarySelect },
        },
    },
    list: {
        pagination: { pageSize: 20 },
    },
    // «Експорт в Excel» (`SPEC-009` §12): поточна вибірка таблиці файлом
    // «Клієнти.xlsx»; кнопку видно за колекційним правом `export` (A)
    headerPage: {
        eventHandlers: {
            onButtons: {
                ExportToExcel: exportToExcelButton('Клієнти.xlsx'),
            },
        },
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                ClientPayload: prepareClientPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            // Кнопки верифікації — лише в перегляді й лише за `_permissions`
            // запису (`SPEC-009` §8.6): перехід не має йти з незбереженими правками
            view: { template: ClientFormTemplate, onButtons: ClientVerificationButtons },
            edit: { template: ClientFormTemplate },
            create: { template: ClientFormTemplate },
        },
    },
    drawer: {
        // Як картка компанії: два стовпці блоків і поля по два в ряд
        width: 1200,
    },
    restUrl: CLIENT_REST_URL,
    pageUrl: '/clients',
    pageConfig: {
        title: 'Клієнти',
        icon: <IoPeopleOutline />,
    },
    settings: {
        searchFields: ['title', 'fullName'],
    },
}
